import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FoodaholixService } from 'app/core/services';
import { FuseAlertType } from '@fuse/components/alert';
import { filter, switchMap } from 'rxjs/operators';
import { Observable } from 'rxjs';

export interface BulkUploadImageDialogData {
  entityIds: string[];
  entityType: 'post' | 'restaurant';
  uploadFunction: (
    image: File,
    entityIds: string[],
    isAiImage: boolean
  ) => Observable<{ count: number; message: string }>;
}

@Component({
  selector: 'app-bulk-upload-image-dialog',
  templateUrl: './bulk-upload-image-dialog.component.html',
})
export class BulkUploadImageDialogComponent implements OnInit {
  form: FormGroup;
  isLoading: boolean = false;
  alert: { type: FuseAlertType; message: string } = { type: 'success', message: '' };
  showAlert: boolean = false;
  selectedFile: File | null = null;
  imagePreview: string | ArrayBuffer | null = null;
  entityIds: string[] = [];
  entityType: 'post' | 'restaurant' = 'post';
  totalEntities: number = 0;
  uploadFunction: (
    image: File,
    entityIds: string[],
    isAiImage: boolean
  ) => Observable<{ count: number; message: string }>;

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  constructor(
    private _fb: FormBuilder,
    private _foodaholixService: FoodaholixService,
    private _matSnackbar: MatSnackBar,
    private _changeDetectorRef: ChangeDetectorRef,
    private _dialogRef: MatDialogRef<BulkUploadImageDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BulkUploadImageDialogData
  ) {
    this.entityIds = data.entityIds || [];
    this.entityType = data.entityType || 'post';
    this.uploadFunction = data.uploadFunction;
    this.totalEntities = this.entityIds.length;
  }

  ngOnInit(): void {
    this.form = this._fb.group({
      image: [null, Validators.required],
      isAiImage: [false],
    });
  }

  get entityLabel(): string {
    return this.entityType === 'post' ? 'Post' : 'Restaurant';
  }

  get entityLabelPlural(): string {
    return this.entityType === 'post' ? 'Posts' : 'Restaurants';
  }

  /**
   * Handle file selection
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        this.alert = {
          type: 'error',
          message: 'Invalid file type. Please select a valid image (JPG, PNG, WEBP, SVG).',
        };
        this.showAlert = true;
        setTimeout(() => (this.showAlert = false), 5000);
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        this.alert = {
          type: 'error',
          message: 'File size exceeds 10MB. Please select a smaller image.',
        };
        this.showAlert = true;
        setTimeout(() => (this.showAlert = false), 5000);
        return;
      }

      this.selectedFile = file;
      this.form.patchValue({ image: file });
      this.form.get('image').markAsTouched();

      // Generate image preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.imagePreview = e.target.result;
        this._changeDetectorRef.markForCheck();
      };
      reader.readAsDataURL(file);
    }
  }

  /**
   * Clear selected file
   */
  clearFile(): void {
    this.selectedFile = null;
    this.imagePreview = null;
    this.form.patchValue({ image: null });
    this.form.get('image').markAsUntouched();

    // Reset the file input element
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }

    this._changeDetectorRef.markForCheck();
  }

  /**
   * Upload image to selected entities
   */
  upload(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alert = {
        type: 'error',
        message: 'Please select an image to upload.',
      };
      this.showAlert = true;
      setTimeout(() => (this.showAlert = false), 5000);
      return;
    }

    if (this.totalEntities === 0) {
      this.alert = {
        type: 'error',
        message: `No ${this.entityLabelPlural.toLowerCase()} selected.`,
      };
      this.showAlert = true;
      setTimeout(() => (this.showAlert = false), 5000);
      return;
    }

    // Confirmation with count
    this._foodaholixService
      .confirmationPopup(
        `Are you sure you want to upload this image to ${
          this.totalEntities
        } selected ${this.entityLabelPlural.toLowerCase()}?`,
        'Confirm Upload'
      )
      .pipe(
        filter((result) => result === 'yes'),
        switchMap(() => {
          this.isLoading = true;
          this.showAlert = false;
          this._changeDetectorRef.markForCheck();
          const isAiImage = this.form.get('isAiImage').value || false;
          return this.uploadFunction(this.selectedFile, this.entityIds, isAiImage);
        })
      )
      .subscribe(
        (uploadResponse) => {
          this.isLoading = false;
          this._matSnackbar.open(
            uploadResponse.message ||
              `Successfully uploaded image to ${uploadResponse.count} ${this.entityLabelPlural.toLowerCase()}!`,
            '',
            {
              duration: 5000,
              panelClass: ['bg-primary'],
            }
          );

          // Close dialog and return success
          this._dialogRef.close(true);
        },
        (error) => {
          this.isLoading = false;
          const errorMessage =
            error?.error?.error || error?.error?.message || error?.message || 'Failed to upload image';
          this.alert = {
            type: 'error',
            message: errorMessage,
          };
          this.showAlert = true;
          setTimeout(() => (this.showAlert = false), 5000);
          this._changeDetectorRef.markForCheck();
        }
      );
  }

  /**
   * Close dialog
   */
  close(): void {
    this._dialogRef.close(false);
  }
}
